import React, { useEffect, useState } from 'react';
import { getPortions } from '../services/portionService';
import { createPortionSupply } from '../services/portionSupplyService';

const today = () => new Date().toISOString().slice(0, 10);

export default function PortionSupplyFormModal({ mobileStandId, onClose, onSaved }) {
  const [portions, setPortions] = useState([]);
  const [date, setDate] = useState(today);
  const [observations, setObservations] = useState('');
  const [details, setDetails] = useState([{ portionId: '', quantity: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getPortions()
      .then(data => setPortions(Array.isArray(data) ? data.filter(p => p.state) : []))
      .catch(() => {});
  }, []);

  function handleDetailChange(index, field, value) {
    setDetails(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
  }

  function addLine() {
    setDetails(prev => [...prev, { portionId: '', quantity: '' }]);
  }

  function removeLine(index) {
    setDetails(prev => prev.filter((_, i) => i !== index));
  }

  const validLines = details.filter(d => d.portionId && d.quantity);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!validLines.length) {
      setError('Debe agregar al menos una porción con cantidad.');
      return;
    }

    setSaving(true);
    try {
      await createPortionSupply({
        mobileStandId,
        date,
        observations: observations.trim(),
        details: validLines.map(d => ({
          portionId: Number(d.portionId),
          quantity: Number(d.quantity),
        })),
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo registrar el abastecimiento.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="section-card"
        style={{ width: '100%', maxWidth: 640, marginBottom: 0, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="section-header">
          <h6>
            <i className="bi bi-truck" style={{ color: 'var(--orig-green)' }} />
            Registrar Abastecimiento
          </h6>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--orig-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
            title="Cerrar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="section-body">
            {error && (
              <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
                <i className="bi bi-exclamation-triangle-fill" />
                {error}
              </div>
            )}

            <div className="row g-3" style={{ marginBottom: 18 }}>
              <div className="col-12 col-md-5">
                <label className="form-label">Fecha</label>
                <input
                  type="date"
                  className="form-control"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  required
                />
              </div>
              <div className="col-12 col-md-7">
                <label className="form-label">
                  Observaciones <span style={{ opacity: 0.5, fontSize: '0.8em' }}>(opcional)</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Entrega diaria"
                  value={observations}
                  onChange={e => setObservations(e.target.value)}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--orig-text)' }}>Porciones</span>
              <button type="button" className="btn-orig btn-orig-green" onClick={addLine} style={{ padding: '5px 12px', fontSize: 12 }}>
                <i className="bi bi-plus-lg" />
                Agregar línea
              </button>
            </div>

            <table className="orig-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Porción</th>
                  <th style={{ width: 130 }}>Cantidad</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {details.map((line, idx) => (
                  <tr key={idx}>
                    <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>
                      {idx + 1}
                    </td>
                    <td>
                      <select
                        className="form-control"
                        value={line.portionId}
                        onChange={e => handleDetailChange(idx, 'portionId', e.target.value)}
                        required
                        style={{ marginBottom: 0 }}
                      >
                        <option value="">Seleccione una porción</option>
                        {portions.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name}{p.unitType ? ` — ${p.unitType}` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        placeholder="0"
                        min="0.01"
                        value={line.quantity}
                        onChange={e => handleDetailChange(idx, 'quantity', e.target.value)}
                        required
                        style={{ marginBottom: 0 }}
                      />
                    </td>
                    <td>
                      {details.length > 1 && (
                        <button
                          type="button"
                          className="btn-orig btn-orig-outline"
                          onClick={() => removeLine(idx)}
                          style={{ padding: '5px 10px', color: 'var(--orig-red-light)' }}
                          title="Eliminar línea"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 10,
            padding: '14px 20px', borderTop: '1px solid var(--orig-border)',
          }}>
            <button type="button" className="btn-orig btn-orig-outline" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-orig btn-orig-green" disabled={saving}>
              <i className="bi bi-send" />
              {saving ? 'Registrando...' : 'Registrar Abastecimiento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
